// PRD 07 §3.2 / §4.2 — Image content block editor.
//   Title + click-to-upload area (image/*, max 5MB) + showText toggle +
//   optional description textarea. The file is uploaded (FEATURE_061) and the
//   returned URL is stored on `block.dataUrl` (mock mode returns a base64 dataUrl).

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';

import { toast } from '@/components/feedback/useToast';

import { managementGuidelinesApi } from '../api/management-guidelines.adapter';
import type { ContentBlock } from '../types/management-guidelines.types';

const IMAGE_MAX_BYTES = 5 * 1024 * 1024;
// SHCT-640 — JPG/PNG only, matching ADM.017, PRD 07 §3.2 and the BE's
// `BlockMediaService` MediaType enum. The old guard was `type.startsWith('image/')`,
// which waved GIF through to the server just to have it rejected with a 422.
const IMAGE_TYPES = ['image/jpeg', 'image/png'];

interface ImageBlockEditorProps {
  block: ContentBlock;
  index: number;
  onChange: (patch: Partial<ContentBlock>) => void;
  onDelete: () => void;
  /** SHCT-582 — PRD 21 §7.4 P-2: locked for a METADATA:READ operator. */
  disabled?: boolean;
}

export function ImageBlockEditor({
  block,
  index,
  onChange,
  onDelete,
  disabled = false,
}: ImageBlockEditorProps) {
  const { t } = useTranslation('management-guidelines');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File | null) => {
    if (!file) return;
    // ADM.017 — JPG/PNG only + 5MB, pre-checked client-side for instant feedback
    // (the BE re-validates authoritatively). Every failure — wrong format, over
    // size, or a failed upload — surfaces the SAME message, which is what ADM.017
    // confirmed: "이미지 업로드에 실패했습니다. 파일 형식과 크기를 확인해 주세요."
    // Upload errors have no bound input field, so a toast is the right channel
    // (admin-web §7.1 file-upload exception). Mirrors counseling-manual's editor.
    if (!IMAGE_TYPES.includes(file.type) || file.size > IMAGE_MAX_BYTES) {
      toast.error(t('uploadErrors.imageFailed'));
      return;
    }
    try {
      const url = await managementGuidelinesApi.uploadMedia(file);
      onChange({ dataUrl: url });
    } catch {
      toast.error(t('uploadErrors.imageFailed'));
    }
  };

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        padding: 8,
        background: '#fff',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>
          {t('blocks.position', {
            index: index + 1,
            type: t('blocks.types.image'),
          })}
        </span>
        <button
          type="button"
          onClick={onDelete}
          className="btn btn-default btn-secondary"
          disabled={disabled}
          style={{
            height: 28,
            padding: '0 8px',
            color: disabled ? undefined : 'var(--destructive)',
          }}
          title={t('blocks.delete')}
          aria-label={t('blocks.delete')}
        >
          <Trash2 size={14} />
        </button>
      </div>

      <input
        type="text"
        className="form-field__input"
        value={block.title}
        onChange={(e) => onChange({ title: e.target.value })}
        placeholder={t('blocks.imageTitlePlaceholder')}
        disabled={disabled}
        style={{ marginBottom: 6 }}
      />

      {/* SHCT-370 — ONE image per block, and NO replacing it in place. PRD 07 §3.2 line 125:
          "1블록 1이미지·교체 불가(업로드 후 재클릭 차단, 바꾸려면 블록 삭제 후 재추가)". Once an image
          exists this is a STATIC preview, not a button: no role="button", no onClick, no keyboard
          handler, and the file input is not reachable from here — so a re-click cannot open the
          picker. To change the picture the author deletes the block (🗑 above) and adds a new one.
          Same shape as the counseling-manual and health-encyclopedia editors, which already comply. */}
      {block.dataUrl ? (
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            padding: 12,
            textAlign: 'center',
            background: 'var(--muted)',
            marginBottom: 6,
          }}
        >
          <img
            src={block.dataUrl}
            alt=""
            style={{
              maxWidth: '100%',
              maxHeight: 160,
              borderRadius: 6,
              display: 'block',
              margin: '0 auto',
            }}
          />
          <div style={{ fontSize: 11, color: 'var(--muted-foreground)', marginTop: 6 }}>
            {t('blocks.imageReplaceHint')}
          </div>
        </div>
      ) : (
        /* Click-to-upload area. Mirrors prototype `data-gupload` div. */
        <div
          role={disabled ? undefined : 'button'}
          tabIndex={disabled ? undefined : 0}
          aria-disabled={disabled || undefined}
          onClick={disabled ? undefined : () => fileInputRef.current?.click()}
          onKeyDown={
            disabled
              ? undefined
              : (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }
                }
          }
          style={{
            border: '1px dashed var(--border)',
            borderRadius: 'var(--radius-sm)',
            padding: 16,
            textAlign: 'center',
            background: 'var(--muted)',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : undefined,
            marginBottom: 6,
          }}
        >
          <div style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>
            {t('blocks.imageUploadHint')}
          </div>
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        // SHCT-640 — narrow the OS file dialog to what is actually accepted, so a
        // GIF is not offered and then rejected. `handleFile` still re-checks: the
        // dialog's filter is a convenience, not a guarantee (drag-drop bypasses it).
        accept="image/jpeg,image/png"
        style={{ display: 'none' }}
        onChange={(e) => {
          void handleFile(e.target.files?.[0] ?? null);
          // Reset so picking the same file again still triggers `onChange`.
          e.target.value = '';
        }}
      />

      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 12,
          cursor: 'pointer',
          marginBottom: 4,
        }}
      >
        <input
          type="checkbox"
          checked={block.showText}
          onChange={(e) => onChange({ showText: e.target.checked })}
          disabled={disabled}
          style={{ width: 14, height: 14, accentColor: 'var(--info)' }}
        />
        {t('blocks.showText')}
      </label>
      {block.showText ? (
        <textarea
          className="form-field__textarea"
          style={{ minHeight: 60 }}
          value={block.text}
          onChange={(e) => onChange({ text: e.target.value })}
          placeholder={t('blocks.imageDescPlaceholder')}
          disabled={disabled}
        />
      ) : null}
    </div>
  );
}
